import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Stepper,
  Step,
  StepLabel,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  MoneyOff as RefundIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ordersService, { Order, OrderStatus, OrderHistory, OrderRefund } from '../../services/orders.service';

const OrderDetails: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Status change dialog
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus | ''>('');
  const [statusDescription, setStatusDescription] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);

  // Refund dialog
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [creatingRefund, setCreatingRefund] = useState(false);

  const statusSteps: OrderStatus[] = ['pendente', 'confirmado', 'preparando', 'enviado', 'entregue'];

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ordersService.getOrder(Number(id));
      if (response.success) {
        setOrder(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching order:', err);
      setError(t('shop.errorLoadingOrder'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleStatusChange = async () => {
    if (!order || !newStatus) return;
    setChangingStatus(true);
    try {
      await ordersService.updateOrderStatus(order.id, newStatus, statusDescription);
      setSuccessMessage(t('shop.statusUpdated'));
      setStatusDialog(false);
      setNewStatus('');
      setStatusDescription('');
      fetchOrder();
    } catch (err: any) {
      setError(t('shop.errorUpdatingStatus', { message: err.message || t('common.error') }));
    } finally {
      setChangingStatus(false);
    }
  };

  const handleCreateRefund = async () => {
    if (!order || !refundAmount || !refundReason) return;
    setCreatingRefund(true);
    try {
      await ordersService.createRefund(order.id, Number(refundAmount), refundReason);
      setSuccessMessage(t('shop.refundCreated'));
      setRefundDialog(false);
      setRefundAmount('');
      setRefundReason('');
      fetchOrder();
    } catch (err: any) {
      setError(t('shop.errorCreatingRefund', { message: err.message || t('common.error') }));
    } finally {
      setCreatingRefund(false);
    }
  };

  const getActiveStep = (): number => {
    if (!order) return 0;
    if (order.status === 'cancelado') return -1;
    return statusSteps.indexOf(order.status);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('shop.orderNotFound')}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/pagalishop/orders')}>
          {t('shop.backToOrders')}
        </Button>
      </Box>
    );
  }

  const availableStatuses = ordersService.getNextStatuses(order.status);

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/pagalishop/orders');
          }}
        >
          {t('shop.breadcrumbOrders')}
        </Link>
        <Typography color="text.primary">#{order.numero}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/pagalishop/orders')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {t('shop.orderNumber', { number: order.numero })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('shop.createdAt', { date: formatDate(order.createdAt) })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {availableStatuses.length > 0 && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setStatusDialog(true)}
            >
              {t('shop.changeStatus')}
            </Button>
          )}
          {order.status !== 'cancelado' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RefundIcon />}
              onClick={() => setRefundDialog(true)}
            >
              {t('shop.refund')}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Status Stepper */}
      {order.status !== 'cancelado' ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={getActiveStep()} alternativeLabel>
            {statusSteps.map((status) => (
              <Step key={status}>
                <StepLabel>{ordersService.getStatusLabel(status)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      ) : (
        <Alert severity="error" sx={{ mb: 3 }} icon={<CancelIcon />}>
          {t('shop.orderCancelledAlert')}
          {order.cancellationReason && `: ${order.cancellationReason}`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Order Info */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('shop.orderItems')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('shop.productItemHeader')}</TableCell>
                      <TableCell align="center">{t('shop.qtyHeader')}</TableCell>
                      <TableCell align="right">{t('shop.unitPriceHeader')}</TableCell>
                      <TableCell align="right">{t('shop.subtotalHeader')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items && order.items.length > 0 ? (
                      order.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.productImage && (
                                <Avatar
                                  src={item.productImage}
                                  variant="rounded"
                                  sx={{ width: 40, height: 40 }}
                                />
                              )}
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {item.productName}
                                </Typography>
                                {item.notes && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.notes}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">
                            {ordersService.formatCurrency(item.unitPrice)}
                          </TableCell>
                          <TableCell align="right">
                            {ordersService.formatCurrency(item.subtotal)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t('shop.noItemsRecorded')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              {/* Totals */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 4, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('shop.subtotalLabel')}
                  </Typography>
                  <Typography variant="body2">
                    {ordersService.formatCurrency(order.subtotal)}
                  </Typography>
                </Box>
                {order.deliveryFee > 0 && (
                  <Box sx={{ display: 'flex', gap: 4, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('shop.deliveryFeeLabel')}
                    </Typography>
                    <Typography variant="body2">
                      {ordersService.formatCurrency(order.deliveryFee)}
                    </Typography>
                  </Box>
                )}
                {order.discount > 0 && (
                  <Box sx={{ display: 'flex', gap: 4, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('shop.discountLabel')}
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      -{ordersService.formatCurrency(order.discount)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ width: 200, my: 1 }} />
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Typography variant="h6">{t('shop.totalLabel')}</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {ordersService.formatCurrency(order.total)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* History */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('shop.historyTitle')}
              </Typography>
              {order.history && order.history.length > 0 ? (
                <List dense>
                  {order.history.map((entry) => (
                    <ListItem key={entry.id} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          <HistoryIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {entry.previousStatus && (
                              <>
                                <Chip
                                  size="small"
                                  label={ordersService.getStatusLabel(entry.previousStatus as OrderStatus)}
                                  variant="outlined"
                                />
                                →
                              </>
                            )}
                            <Chip
                              size="small"
                              label={ordersService.getStatusLabel(entry.newStatus as OrderStatus)}
                              color={ordersService.getStatusColor(entry.newStatus as OrderStatus)}
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            {entry.description && <Typography variant="body2">{entry.description}</Typography>}
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(entry.createdAt)} {entry.userName && `${t('shop.byLabel')} ${entry.userName}`}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('shop.noHistoryChanges')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('shop.currentStatus')}
              </Typography>
              <Chip
                label={ordersService.getStatusLabel(order.status)}
                color={ordersService.getStatusColor(order.status)}
                sx={{ fontWeight: 600 }}
              />
            </CardContent>
          </Card>

          {/* Store */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <StoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('shop.storeSection')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {order.storeLogo && (
                  <Avatar src={order.storeLogo} sx={{ width: 48, height: 48 }} />
                )}
                <Typography variant="body1" fontWeight={500}>
                  {order.storeName || t('shop.storeNotIdentified')}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Customer */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('shop.customerSection')}
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <PersonIcon fontSize="small" color="action" />
                  </ListItemAvatar>
                  <ListItemText primary={order.clientName || t('shop.notInformed')} />
                </ListItem>
                {order.clientPhone && (
                  <ListItem disableGutters>
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <PhoneIcon fontSize="small" color="action" />
                    </ListItemAvatar>
                    <ListItemText primary={order.clientPhone} />
                  </ListItem>
                )}
                {order.clientEmail && (
                  <ListItem disableGutters>
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <EmailIcon fontSize="small" color="action" />
                    </ListItemAvatar>
                    <ListItemText primary={order.clientEmail} />
                  </ListItem>
                )}
                {order.deliveryAddress && (
                  <ListItem disableGutters alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <LocationIcon fontSize="small" color="action" />
                    </ListItemAvatar>
                    <ListItemText primary={order.deliveryAddress} />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Payment */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('shop.paymentSection')}
              </Typography>
              <Typography variant="body2">
                {order.paymentMethod
                  ? t('shop.paymentMethod', { method: order.paymentMethod })
                  : t('shop.paymentNotInformed')}
              </Typography>
            </CardContent>
          </Card>

          {/* Refunds */}
          {order.refunds && order.refunds.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <RefundIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('shop.refundsSection')}
                </Typography>
                <List dense>
                  {order.refunds.map((refund) => (
                    <ListItem key={refund.id} disableGutters>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" fontWeight={500}>
                              {ordersService.formatCurrency(refund.amount)}
                            </Typography>
                            <Chip
                              size="small"
                              label={refund.status}
                              color={
                                refund.status === 'processado' ? 'success' :
                                refund.status === 'rejeitado' ? 'error' : 'warning'
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              {refund.reason}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(refund.createdAt)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {order.notes && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('shop.notesSection')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {order.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('shop.changeStatusTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label={t('shop.newStatusLabel')}
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as OrderStatus)}
            sx={{ mt: 2, mb: 2 }}
          >
            {availableStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {ordersService.getStatusLabel(status)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('shop.statusDescriptionLabel')}
            value={statusDescription}
            onChange={(e) => setStatusDescription(e.target.value)}
            placeholder={newStatus === 'cancelado' ? t('shop.cancelReasonPlaceholder') : t('shop.changeDescriptionPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleStatusChange}
            disabled={!newStatus || changingStatus}
            startIcon={changingStatus ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {changingStatus ? t('shop.savingStatus') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog} onClose={() => setRefundDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('shop.createRefundTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('shop.maxRefundAvailable', { amount: ordersService.formatCurrency(order.total) })}
          </Alert>
          <TextField
            fullWidth
            type="number"
            label={t('shop.refundAmountLabel')}
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0, max: order.total, step: 0.01 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('shop.refundReasonLabel')}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder={t('shop.refundReasonPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCreateRefund}
            disabled={!refundAmount || !refundReason || creatingRefund || Number(refundAmount) > order.total}
            startIcon={creatingRefund ? <CircularProgress size={16} /> : <RefundIcon />}
          >
            {creatingRefund ? t('shop.creatingRefund') : t('shop.createRefundButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderDetails;
